/* eslint-disable prettier/prettier */
/* eslint-disable */

// React hooks
import { useEffect } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Material Kit 2 React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import FilledInfoCard from "examples/Cards/InfoCards/FilledInfoCard";

// Hjem page sections
import Information from "pages/Hjem/sections/Information";

// Routes
import routes from "routes";
import Footer from "footer.routes";
import discoFlash from "./theme";

// GSAP for animasjoner
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import useScrollFadeIn from "hooks/useScrollFadeIn";
import useFadeInOnLoad from "hooks/useFadeInOnLoad"; // Ny hook for fade-in on load

gsap.registerPlugin(ScrollTrigger);

// Images
import bgImage from "assets/images/backgrounds/t-event-home-bg-cropped.jpg";
import logo from "assets/images/logos/T-event/fulllogo_transparent.png";
import midSectionImage from "assets/images/hjem/midsection/t-event-midsection-whole.jpg";


// SEO metadata
import { Helmet } from "react-helmet";

function Hjem() {
  // Bruker useFadeInOnLoad for elementer som skal fades inn ved sidens last
  const logoRef = useFadeInOnLoad(0.3);
  const headingRef = useFadeInOnLoad(0.6);
  const subheadingRef = useFadeInOnLoad(0.9);

  // Bruker useScrollFadeIn for elementer som fades inn ved scroll
  const infoRef = useScrollFadeIn();
  const midSectionRef = useScrollFadeIn();
  const bookingRef = useScrollFadeIn();

  // Parallax-effekt for midtseksjonen
  useEffect(() => {
    // Parallax effekt på midtseksjons bildet
    gsap.to(".midsection-image", {
      scrollTrigger: {
        trigger: ".midsection-image", // Hvilket element som skal trigge scrollen
        start: "top bottom", // Start når toppen av elementet er nederst på skjermen
        end: "bottom top", // Slutt når bunnen av elementet er øverst på skjermen
        scrub: true, // Gir en smidig effekt som følger med rullingen
      },
      y: "-10%", // Flytter bakgrunnen opp med 30% for å skape parallax-effekt
      ease: "none", // Ingen easing for smooth movement
    });
  }, []);

  useEffect(() => {
    // Start fade-in animasjon når midtseksjonen er synlig i viewporten
    gsap.fromTo(
      ".midsection-image",
      { opacity: 0 },
      {
        opacity: 1,
        duration: 1.5,  // Myk overgang på 1.5 sekunder
        ease: "power2.out",  // Bruker "power2.out" for en myk utgang på animasjonen
        scrollTrigger: {
          trigger: ".midsection-image",
          start: "top 100%",  // Starter animasjonen når toppen av bildet er 75% synlig
          end: "bottom top",
          toggleActions: "play none none reverse",  // Starter når scroll trigger er aktiv
        },
      }
    );
  }, []);
  

  return (
    <> 
    <Helmet>  
      <title>T-Events - Lyd, Lys, Stemning | Profesjonelle Eventtjenester</title>
      <meta
        name="description"
        content="T-Events leverer profesjonell lyd, lys og underholdning for alle typer arrangementer. Skap magiske øyeblikk for ditt event!"
      />
      <link rel="canonical" href="https://t-events.no/" />
    </Helmet>     

    
      <DefaultNavbar routes={routes} />

      {/* HERO SECTION */}
      <MKBox
          minHeight="100vh"
          width="100%"
          position="relative"
          sx={{
            backgroundImage: `url(${bgImage})`,
            backgroundSize: "cover",  // Sørger for at bildet dekker hele området
            backgroundPosition: { xs: "center center", sm: "center center", md: "center center" },  // Bildet er alltid sentrert
            backgroundAttachment: { xs: "scroll", md: "fixed" },
            backgroundRepeat: "no-repeat",
            display: "grid",
            placeItems: "center",
            overflow: "hidden",
          }}
        >
        {/* Mørkt overlay */}
        <MKBox
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.6)", 
            zIndex: 1,
          }}
        />

        {/* Disco Overlay */}
        <MKBox
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            mixBlendMode: "overlay",
            animation: `${discoFlash} 2s infinite alternate ease-in-out`,
            zIndex: 2,
          }}
        />

        <Container sx={{ position: "relative", zIndex: 3, textAlign: "center" }}>
          <Grid container direction="column" alignItems="center" justifyContent="center" xs={12} lg={7} mx="auto">
            
            {/* Logo fades inn ved sidens load */}
            <MKBox ref={logoRef} component="img" src={logo} alt="T-Events Logo" width={{ xs: "200px", sm: "250px", md: "350px" }} mb={2} sx={{
              filter: "brightness(0) invert(1)",
              display: "block",
              mx: "auto",
              marginTop: { xs: "40px", md: "80px" },
            }} />

            {/* Overskrift fades inn ved sidens load */}
            <MKTypography ref={headingRef} variant="h1" color="white" textAlign="center" px={{ xs: 4, lg: 10 }} mt={1} sx={{
              fontSize: { xs: "28px", sm: "36px", md: "42px" },
              fontWeight: 200,
            }}>
              LYD. LYS. STEMNING.
            </MKTypography>

            {/* Undertekst fades inn ved sidens load */}
            <MKTypography ref={subheadingRef} variant="h5" color="white" textAlign="center" px={{ xs: 4, lg: 10 }} mt={1} sx={{ 
              fontSize: '18px',                 
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 300,
            }}>
              Vi skaper minnerike øyeblikk for ditt arrangement!
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>

      {/* INFORMASJONSKORT */}
      <Card ref={infoRef} sx={{ p: 2, mx: { xs: 2, lg: 3 }, mt: -6, mb: 4, position: "relative", zIndex: 4 }}>
        <Information />
      </Card>

      {/* MIDSECTION IMAGE med Parallax-effekt og Fade-in */}
      <MKBox className="midsection-image" minHeight="60vh" width="100%" position="relative" sx={{
        backgroundImage: `url(${midSectionImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
        zIndex: 2,
        marginTop: "-80px",
      }}>
        <MKBox sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "rgba(0, 0, 0, 0.4)",
          zIndex: 1,
        }} />
        <Container sx={{ position: "relative", zIndex: 2, textAlign: "center", py: 6 }}>
          <MKTypography variant="h3" color="white" fontWeight="bold" mb={2}>
            Egne Events
          </MKTypography>
          <MKTypography color="white" fontSize={{ xs: "16px", sm: "18px" }} mb={3} sx={{ maxWidth: "700px", mx: "auto", lineHeight: 1.7 }}>
            I tillegg til utleie og oppdrag for andre, hoster T-Event egne arrangementer. Fra takeover på uteplasser til Halloween-rave på Park22 – vi vet hva som skal til for å skape en kveld folk snakker om lenge etterpå.
          </MKTypography>
          <MKTypography
            component="a"
            href="https://www.facebook.com/teventservice/events"
            target="_blank"
            rel="noopener noreferrer"
            variant="body1"
            color="white"
            sx={{ textDecoration: "underline", fontWeight: "bold" }}
          >
            Se kommende events
          </MKTypography>
        </Container>
      </MKBox>

      {/* BOOKING-KORT */}
      <Card ref={bookingRef} sx={{ p: 2, mx: { xs: 2, lg: 3 }, mt: -10, mb: 4, position: "relative", zIndex: 5 }}>
      <Container>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={4}>
            <FilledInfoCard
              isBooking={true}
              variant="gradient"
              color="info"
              icon="event"
              title="Book Event"
              description="Trenger du profesjonell lyd og lys til ditt arrangement?"
            />
            </Grid>
            <Grid item xs={12} lg={4}>
            <FilledInfoCard
              color="info"
              icon="music_note"
              title="Lei DJ"
              description="Lei en profesjonell DJ som sørger for fantastisk stemning!"
              action={{
                type: "internal",
                route: "/kontakt-oss?type=dj", // Sender med type som parameter
                  label: "Kontakt for DJ",
              }}
            />
            </Grid>
            <Grid item xs={12} lg={4}>
            <FilledInfoCard
              color="info"
              icon="lightbulb"
              title="Utleie av utstyr"
              description="Lys, lyd, røykmaskiner og mer!"
              action={{
                type: "internal",
                route: "/kontakt-oss?type=eventutstyr", // Sender med type som parameter
                label: "Kontakt for utstyr",
              }}
            />
            </Grid>
          </Grid>
        </Container>
      </Card>

      <MKBox pt={6} px={1} mt={6}>
        <Footer />
      </MKBox>
    </>
  );
}

export default Hjem;
